import React, { useEffect, useRef, useState } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationCenter from './NotificationCenter';

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className }) => {
  const { notifications, unreadCount, isConnected, lastArrivalAt } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Phase C.14: clicking the gear icon in the dropdown jumps to the
  // Settings page where `NotificationPreferencesTable` lives. Without
  // this prop the icon never renders, so users couldn't get to opt-in
  // toggles without knowing the /settings route.
  const handleTogglePreferences = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  // Phase C.26: animate the bell only on fresh ADD_NOTIFICATION
  // arrivals (`lastArrivalAt` bumps from reducer). Previously this
  // watched `unreadCount > lastUnreadCount` which fired false
  // positives whenever Phase C.11 periodic resync (SET_UNREAD_COUNT)
  // brought the count up from another tab — user got ringing bell
  // even though nothing arrived here.
  useEffect(() => {
    if (lastArrivalAt === 0) return;
    setHasNewNotifications(true);
    const timer = setTimeout(() => setHasNewNotifications(false), 2000);
    return () => clearTimeout(timer);
  }, [lastArrivalAt]);

  // Phase C.96: single-pass boolean check instead of filter().length.
  // Only the boolean is used downstream — the materialized array was
  // discarded immediately after.
  const hasCriticalNotifications = notifications.some(
    (n) => !n.read && n.priority === 'critical'
  );

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition-colors',
          'hover:bg-slate-100 hover:text-slate-900',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40',
          isOpen && 'bg-slate-100 text-slate-900',
          className
        )}
        aria-label={`Notifications (${unreadCount} unread)`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {hasNewNotifications || hasCriticalNotifications ? (
          <BellRing
            className={cn(
              'h-[18px] w-[18px]',
              hasCriticalNotifications && 'text-rose-600',
              hasNewNotifications && 'animate-bounce'
            )}
            aria-hidden="true"
          />
        ) : (
          <Bell className="h-[18px] w-[18px]" aria-hidden="true" />
        )}

        {/* Unread dot (count lives in the aria-label and the menu header) */}
        {unreadCount > 0 && (
          <span className="absolute right-2 top-[7px] flex h-2 w-2" aria-hidden="true">
            {hasCriticalNotifications && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
            )}
            <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-600 ring-2 ring-white" />
          </span>
        )}
      </button>

      {/* Notification Center Dropdown */}
      <NotificationCenter
        isOpen={isOpen}
        onClose={handleClose}
        onTogglePreferences={handleTogglePreferences}
        anchorRef={buttonRef}
      />

      {/* Screen Reader Announcements */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {hasNewNotifications && `New notification received. ${unreadCount} unread notifications.`}
        {hasCriticalNotifications && `Critical notification requires attention.`}
        {!isConnected && 'Real-time notifications disconnected.'}
      </div>
    </div>
  );
};

export default NotificationBell;
